import dayjs, { Dayjs } from "dayjs"
import { useEffect, useRef } from "react"
import {
  Insight_Articles,
  Insight_Hows,
  Insight_Questions,
  Insight_Whys,
  Maybe,
  NudgeFragment,
  NudgeScheduleFragment,
  QuestionnaireFragment,
  QuestionnaireScheduleFragment,
  QuestionnaireTakeFragment,
} from "../generated/graphql"
import { QuestionnaireType } from "./constants"
import { colors } from "./theme"
import {
  DailyDoseResponse,
  DataItem,
  ErrorResponse,
  MoodBarChartData,
  MoodMeasurement,
  Order,
  QuestionnaireResponseProps,
  ScheduleItem,
  TechniquesAndInterventionsTableData,
  TechniquesAndInterventionType,
  UnscheduledItem,
  UserInsightsHowTableData,
  UserInsightsQuestionTableData,
  UserInsightsResourceTableData,
} from "./types"
import { findIndex } from "lodash"
import { getMoodMeasurementReportedDate } from "./moodChart"

export enum DAYS_OF_WEEK {
  SUNDAY = "Su",
  MONDAY = "Mo",
  TUESDAY = "Tu",
  WEDNESDAY = "We",
  THURSDAY = "Th",
  FRIDAY = "Fr",
  SATURDAY = "Sa",
}

export const TwoCharDayFormatDays = [
  DAYS_OF_WEEK.SUNDAY.toString(),
  DAYS_OF_WEEK.TUESDAY.toString(),
  DAYS_OF_WEEK.THURSDAY.toString(),
  DAYS_OF_WEEK.SATURDAY.toString(),
]

export function getWeekOfData(data: MoodBarChartData[]) {
  const oneWeekAgo = dayjs()
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
    .subtract(1, "week")
  const newData: MoodBarChartData[] = []
  for (let i = 0; i < 7; i++) {
    const day = oneWeekAgo.add(i, "days")
    const date = day.format("YYYY-MM-DD")
    const dataDay = data.find(
      (d) => dayjs(d.createdOn).format("YYYY-MM-DD") === date
    )
    if (dataDay) {
      newData.push(dataDay)
    } else {
      newData.push({
        day: day.format("dd"),
        moodLevel: 0,
        fill: "transparent",
      })
    }
  }
  return newData
}

export function getMonthOfData(data: MoodBarChartData[]) {
  const oneMonthAgo = dayjs()
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0)
    .subtract(30, "days")
  const newData: MoodBarChartData[] = []
  for (let i = 1; i < 31; i++) {
    const day = oneMonthAgo.add(i, "days").format("YYYY-MM-DD")
    const dataDay = data.find(
      (d) => dayjs(d.createdOn).format("YYYY-MM-DD") === day
    )
    if (dataDay) {
      newData.push({
        ...dataDay,
        day: dayjs(dataDay.createdOn).format("YYYY-MM-DD"),
      })
    } else {
      newData.push({
        day,
        moodLevel: 0,
        fill: "transparent",
      })
    }
  }
  return newData
}

export function getDataMoodLevels(
  moodMeasurements: MoodMeasurement[]
): MoodBarChartData[] {
  const moodData: MoodBarChartData[] = []
  moodMeasurements.forEach((mood) => {
    if (!mood.client_created_on) {
      return
    }
    const moodLevel =
      mood.enjoyment === mood.growth
        ? mood.enjoyment + 1
        : (mood.growth ?? 0) > mood.enjoyment
        ? mood.growth
          ? mood.growth + 1
          : 0
        : mood.enjoyment + 1
    const createdOn = getMoodMeasurementReportedDate(mood)
    const fill =
      mood.enjoyment === 0 && mood.growth === 0
        ? colors.neutral.clay
        : mood.enjoyment > (mood.growth ?? 0)
        ? colors.tertiary.orange
        : (mood.growth ?? 0) > mood.enjoyment
        ? colors.primary.periwinkle
        : colors.tertiary.violet
    moodData.push({
      day: createdOn.format("dd"),
      moodLevel,
      fill,
      createdOn: createdOn.format(),
      label: 1, // label is unused but required
    })
  })
  return moodData
}

export const isErrorResponse = (
  response: any | ErrorResponse
): response is ErrorResponse => {
  return (response as ErrorResponse).error !== undefined
}

export const isNudge = (
  item: NudgeFragment | QuestionnaireFragment
): item is NudgeFragment => (item as NudgeFragment).domain !== undefined

export const isNudgeSchedule = (
  item: ScheduleItem
): item is NudgeScheduleFragment =>
  (item as NudgeScheduleFragment).nudge !== undefined

export const isQuestionnaireSchedule = (
  item: ScheduleItem
): item is QuestionnaireScheduleFragment =>
  (item as QuestionnaireScheduleFragment).questionnaire !== undefined

export const isScheduleItem = (
  item: ScheduleItem | UnscheduledItem
): item is ScheduleItem => (item as ScheduleItem).scheduled_at !== undefined

export function getQuestionnaireColor(score: Maybe<number> | undefined) {
  switch (score) {
    case 0:
      return colors.questionnaires.green
    case 1:
      return colors.questionnaires.yellow
    case 2:
      return colors.questionnaires.red
    default:
      return colors.questionnaires.gray
  }
}

export function getQuestionnaireSeverity(score: Maybe<number> | undefined) {
  switch (score) {
    case 0:
      return "Low"
    case 1:
      return "Moderate"
    case 2:
      return "High"
    default:
      return "Missing"
  }
}

export function sortQuestionnaireTake(
  a: QuestionnaireTakeFragment,
  b: QuestionnaireTakeFragment
) {
  return dayjs(a.completed_at).isAfter(dayjs(b.completed_at))
    ? -1
    : dayjs(a.completed_at).isBefore(dayjs(b.completed_at))
    ? 1
    : 0
}

export function getQuestionnaireScore(takes: QuestionnaireTakeFragment[]) {
  if (takes.length === 0) {
    return {
      score: -1,
      date: "",
    }
  }
  const scores: number[] = []
  takes.forEach((take) => {
    let takeScore = 0
    take.questionnaire_responses.forEach(
      (response) => (takeScore += response.response)
    )
    scores.push(takeScore)
  })
  let totalScore = 0
  scores.forEach((score) => (totalScore += score))
  return {
    score: Math.floor(totalScore / scores.length),
    date: takes[0].completed_at,
  }
}

export function descendingComparator<Key extends keyof any>(
  a: { [key in Key]: DataItem },
  b: { [key in Key]: DataItem },
  orderBy: keyof { [key in Key]: DataItem }
) {
  if (b[orderBy].sortData < a[orderBy].sortData) {
    return -1
  }
  if (b[orderBy].sortData > a[orderBy].sortData) {
    return 1
  }
  return 0
}

export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: DataItem }, b: { [key in Key]: DataItem }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

export function stableSort<Key extends keyof any>(
  array: { [key in Key]: DataItem }[],
  comparator: (
    a: { [key in Key]: DataItem },
    b: { [key in Key]: DataItem }
  ) => number
) {
  const stabilizedThis = array.map(
    (el, index) => [el, index] as [{ [key in Key]: DataItem }, number]
  )
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

export function usePrevious<T>(value: T) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

export const isQuestionnaireType = (type: string): type is QuestionnaireType =>
  type === QuestionnaireType.ANXIETY ||
  type === QuestionnaireType.DEPRESSION ||
  type === QuestionnaireType.SUBSTANCE_USE

export function checkTimeForHours(value?: string, unit?: string) {
  const newValue = value ? parseInt(value) : 0
  const isHour = unit === "min" && newValue > 60
  const time = newValue / 60
  const decimal = time % 1
  const hour = time - decimal
  const minute = Math.round(60 * decimal)
  return {
    isHour,
    hour,
    minute,
  }
}

export function getMinutesFromTime(time: number) {
  let decimal = time % 1
  if (decimal < 0) {
    decimal = decimal * -1
  }
  const result = time < 0 ? 60 * (1 - decimal) : 60 * decimal
  return `${Math.round(result)}`.padStart(2, "0")
}

export function getHourFromTime(time: number) {
  let hour = 0
  if ((time >= -12 && time < -11) || (time >= 0 && time < 1)) {
    hour = 12
  } else if ((time >= -11 && time < -10) || (time >= 1 && time < 2)) {
    hour = 1
  } else if ((time >= -10 && time < -9) || (time >= 2 && time < 3)) {
    hour = 2
  } else if ((time >= -9 && time < -8) || (time >= 3 && time < 4)) {
    hour = 3
  } else if ((time >= -8 && time < -7) || (time >= 4 && time < 5)) {
    hour = 4
  } else if ((time >= -7 && time < -6) || (time >= 5 && time < 6)) {
    hour = 5
  } else if ((time >= -6 && time < -5) || (time >= 6 && time < 7)) {
    hour = 6
  } else if ((time >= -5 && time < -4) || (time >= 7 && time < 8)) {
    hour = 7
  } else if ((time >= -4 && time < -3) || (time >= 8 && time < 9)) {
    hour = 8
  } else if ((time >= -3 && time < -2) || (time >= 9 && time < 10)) {
    hour = 9
  } else if ((time >= -2 && time < -1) || (time >= 10 && time < 11)) {
    hour = 10
  } else if ((time >= -1 && time < 0) || (time >= 11 && time < 12)) {
    hour = 11
  }
  return hour
}

export function addMissingPeriodAtEnd(value: string) {
  return value[value.length - 1] !== "." ? `${value}.` : value
}

export const prepareTechniquesAndInterventionsDataTable = (
  nudges: NudgeFragment[],
  //questionnaires: QuestionnaireFragment[],
  user: {
    id: any
    email: any
    firstName: Maybe<string> | undefined
    lastName: Maybe<string> | undefined
    isOrgAdmin: boolean
    organizations:
      | {
          id: number
          name: string
          location: string
        }[]
      | undefined
  }
): TechniquesAndInterventionsTableData[] => {
  const techniquesAndInterventions: TechniquesAndInterventionsTableData[] = []
  nudges.forEach((nudge) => {
    const id = nudge.id
    const domain = nudge.domain
    const technique = nudge.technique
    const active = nudge.active
    const type = TechniquesAndInterventionType.nudge
    const default_messaging = nudge.default_messaging
    const created_by = nudge.created_by
    const resource_id = nudge.resource_id
    const organization_id = nudge?.owner_org_id
    const questionnaire_item = undefined
    const convertedNudge: TechniquesAndInterventionsTableData = {
      id: {
        sortData: id,
        data: id,
      },
      active: {
        sortData: active,
        data: active,
      },
      domain: {
        sortData: domain,
        data: domain,
      },
      technique: {
        sortData: technique,
        data: technique,
      },
      created_by: {
        sortData: created_by,
        data: created_by,
      },
      type: {
        sortData: type,
        data: type,
      },
      default_messaging: {
        sortData: default_messaging,
        data: default_messaging,
      },
      resource_id: {
        sortData: resource_id,
        data: resource_id,
      },
      questionnaire_items: {
        sortData: questionnaire_item,
        data: questionnaire_item,
      },
      more: {
        sortData: "",
        data: {
          organization_id: organization_id,
          practitioner_organization_id: user?.organizations?.[0].id,
        },
      },
    }
    techniquesAndInterventions.push(convertedNudge)
  })
  return techniquesAndInterventions
}
const prefix = "http://"
const prefix2 = "https://"
export const addHttp = (url: string): string =>
  url.toLowerCase().substr(0, prefix.length) !== prefix &&
  url.toLowerCase().substr(0, prefix2.length) !== prefix2
    ? (url = prefix + url)
    : url

export const processWhyStringData = (data?: Insight_Whys[]) => {
  const processed = data?.map((x) => x.resource?.content).join(" ")
  return processed
}
export const processHowStringData = (data?: Insight_Hows[]) => {
  if (!data) return []
  const processed = data?.map(
    (x) =>
      ({
        id: { data: x.resource_id, sortData: x.resource_id },
        string: { data: x.resource.content, sortData: x.resource.content },
      } as UserInsightsHowTableData)
  )
  return processed
}
export const processQuestionStringData = (data?: Insight_Questions[]) => {
  if (!data) return []
  const processed = data?.map(
    (x) =>
      ({
        id: { data: x.resource_id, sortData: x.resource_id },
        string: { data: x.resource?.content, sortData: x.resource?.content },
      } as UserInsightsQuestionTableData)
  )
  return processed
}
export const processResourceData = (data?: Insight_Articles[]) => {
  if (!data) return []
  const processed = data?.map(
    (x) =>
      ({
        id: { data: x.resource.id, sortData: x.resource.id },
        author: { data: x.resource.author, sortData: x.resource.author },
        content: { data: x.resource.content, sortData: x.resource.content },
        content_type: {
          data: x.resource.content_type,
          sortData: x.resource.content_type,
        },
        public: { data: x.resource.public, sortData: x.resource.public },
        owner_org_id: {
          data: x.resource.owner_org_id,
          sortData: x.resource.owner_org_id,
        },
        title: {
          data: x.resource.title,
          sortData: x.resource.title,
        },
        more: {
          data: null,
          sortData: null,
        },
      } as UserInsightsResourceTableData)
  )

  return processed
}

export const dummyResource = {
  id: {
    sortData: -1,
    data: null,
  },
  title: {
    sortData: "‎",
    data: "‎",
  },
  created_on: {
    sortData: "2022-04-08T12:42:44.489281",
    data: "2022-04-08T12:42:44.489281",
  },
  author: {
    sortData: null,
    data: null,
  },
  content: {
    sortData: null,
    data: null,
  },
  content_type: {
    sortData: "text",
    data: "text",
  },
  owner_org_id: {
    sortData: null,
    data: null,
  },
  updated_on: {
    sortData: "2022-04-08T12:42:44.489281",
    data: "2022-04-08T12:42:44.489281",
  },
  created_by: {
    sortData: null,
    data: null,
  },
  public: {
    sortData: true,
    data: true,
  },
  header_image: {
    sortData: null,
    data: null,
  },
  more: { sortData: "", data: {} },
}

export const getTrailingEmptyDailyDoseDaysBeforeDate = (
  indexDate: Dayjs,
  daysAgo: number
) => {
  const trailingDays = []
  for (let i = daysAgo; i--; i >= 0) {
    trailingDays.push({
      date: indexDate.startOf("day").subtract(i, "days"),
      distress: undefined,
    })
  }
  return trailingDays
}

export const clientTrailingDailyDoseDays = (
  indexDate: Dayjs,
  daysAgo: number,
  dailyDoseResponses: DailyDoseResponse[]
) => {
  const emptyTrailingDays = getTrailingEmptyDailyDoseDaysBeforeDate(
    indexDate,
    daysAgo
  )
  return fillDailyDoseDays(dailyDoseResponses, emptyTrailingDays)
}

export const fillDailyDoseDays = (
  dailyDoseResponses: DailyDoseResponse[],
  trailingDays: {
    date: Dayjs
    distress?: number
  }[]
) => {
  dailyDoseResponses.forEach((dose) => {
    const trailingDayIndex = trailingDays.findIndex((day) => {
      const dayIndex = day.date.startOf("day").format("YYYY-MM-DD")
      const dailyDoseIndex = dayjs(dose.response_date)
        .startOf("day")
        .format("YYYY-MM-DD")
      return dayIndex === dailyDoseIndex
    })
    if (trailingDayIndex >= 0)
      trailingDays[trailingDayIndex].distress = dose.response
  })
  return trailingDays
}

/*
  Risk Assessment Questions by display_order:
  1. "In the past week, how often have you thought of killing yourself"
  2. "In the past week, did you make a plan to kill yourself"
  3. "In the past week, did you make an attempt to kill yourself"
*/
export const getRiskAssessmentChartLines = (questionNo: number) => {
  let chartLines: string[] = []
  switch (questionNo) {
    case 1:
      chartLines = ["Never", "Rarely", "Sometimes", "Often", "All of the time"]
      break
    case 2:
      chartLines = ["No", "", "Yes"]
      break
    case 3:
      chartLines = ["No", "", "Yes"]
      break
  }
  return chartLines
}

export const getRiskAssessmentResponseValue = (
  questionnaire_responses: QuestionnaireResponseProps[],
  display_order: number
) => {
  const indx = findIndex(questionnaire_responses, (response: any) => {
    return response.questionnaire_item.display_order === display_order
  })
  const value =
    questionnaire_responses[indx].questionnaire_response_options.response_value
  return value
}

export const getRiskAssessmentQuestion = (
  questionnaire_responses: QuestionnaireResponseProps[],
  display_order: number
) => {
  const indx = findIndex(questionnaire_responses, (response: any) => {
    return response.questionnaire_item.display_order === display_order
  })
  const value = `${questionnaire_responses[indx]?.questionnaire_item.question}?`
  return value
}
